import React, { useCallback, useEffect, useState } from 'react';
import { copilotService, CopilotInteractionRequiredError } from '../services/CopilotService';
import { useAuth } from '../contexts/AuthContext';
import CopilotTimeSuggestions, {
  CopilotSuggestion,
  CurrentUser,
  TimeCatalog,
  filterCustomerTimeSuggestions,
  loadVisibleCatalog,
  stripIds,
} from './CopilotTimeSuggestions';
import ClippyIcon from './ClippyIcon';
import './CopilotDaySummary.css';

const PROMPT = 'Vad har jag gjort idag och hur mycket tid har jag lagt på respektive del?';

// Copilot has no date filter parameter, so the restriction to today has to be spelled out in the prompt
const buildDateScope = (): string => {
  const today = new Date().toLocaleDateString('sv-SE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  return (
    `Använd ENDAST underlag från idag, ${today}, mellan 00:00 och 23:59: mejl skickade eller mottagna idag, ` +
    'Teams-meddelanden skrivna idag och möten som ägde rum idag. Ignorera allt som är äldre, även om det hör till ' +
    'samma tråd eller konversation, och nämn inte tidigare dagar.'
  );
};

const TABLE_FORMAT =
  'Börja svaret direkt med en tabell, utan någon inledande text. Tabellen är mitt förslag på tidrapport för idag ' +
  'och ska ha exakt fyra kolumner i den här ordningen: # | Kund | Vad | Tid. "#" är radens nummer: 1, 2, 3 osv. ' +
  '"Vad" är en kort sammanfattning på högst en mening. ' +
  'Ta inte med aktivitet eller projekt i tabellen. "Tid" anges i timmar, t.ex. 0,5 h. En rad per del av dagen. ' +
  'Sortera raderna efter tid, med mest tid först, och numrera dem efter sorteringen. ' +
  'Skriv inget separat förslag på tidrapport efter tabellen - tabellen är förslaget.';

const INSTRUCTIONS =
  'Föreslå ENDAST tid som tydligt avser arbete åt en extern kund i kundlistan nedan. ' +
  'Föreslå ALDRIG intern tid för Ignia, internt administrativt arbete eller arbete utan en tydligt identifierbar kund. ' +
  'Ta bara med rader där kunden finns i kundlistan; utelämna arbete där kunden är okänd i stället för att skriva "Okänd". ' +
  'När du matchar kunder, utgå i första hand från mina mejl och Teams-konversationer idag (skickade och mottagna mejl, ' +
  'Teams-chattar, kanalinlägg och möten): titta på avsändares och mottagares e-postdomäner och företagsnamn, ' +
  'deltagare i chattar och möten, kanal- och teamnamn, ämnesrader och vilka kund- eller projektnamn som nämns i texten. ' +
  'Efter tabellen: för varje rad, i samma ordning och med radens nummer från tabellen som rubrik ' +
  '(bara siffran, t.ex. "1", inte "Rad 1"), ange kund, projekt och aktivitet (namn från listorna nedan) som passar bäst ' +
  '- skriv "Okänd" om projekt eller aktivitet inte passar, aldrig "null" - ' +
  'och motivera kort med vilket mejl eller vilken Teams-konversation som pekar på kunden. ' +
  'Visa aldrig id:n i tabellen eller texten. ' +
  'Avsluta hela svaret med ett kodblock märkt json (```json ... ```) med tabellens rader i samma ordning, som en lista ' +
  'med objekt: {"customerId": kundens id, "projectId": tal eller null, "activityId": tal eller null, ' +
  '"hours": tal, "description": "samma text som i Vad-kolumnen"}. customerId måste vara en kunds id från kundlistan. ' +
  'Använd bara ett projectId som hör till den valda kunden. Skriv aldrig en rad med null eller okänd customerId. ' +
  'Skriv ingenting efter kodblocket.';

const withId = (title: string, id: number) => `${title} (id ${id})`;

// Lists all customers (with their projects) and all activities with ids, so Copilot can map today's work onto them
const buildCatalogContext = ({ customers, projects, activities }: TimeCatalog): string[] => {
  const projectsByCustomer = new Map<number, string[]>();
  projects.forEach((project) => {
    if (project.Customer) {
      const list = projectsByCustomer.get(project.Customer.Id) ?? [];
      list.push(withId(project.Title, project.Id));
      projectsByCustomer.set(project.Customer.Id, list);
    }
  });

  const customerLines = customers.map((customer) => {
    const customerProjects = projectsByCustomer.get(customer.Id);
    return `- ${withId(customer.Title, customer.Id)}: ${customerProjects ? customerProjects.join(', ') : '(inga pågående projekt)'}`;
  });

  const context: string[] = [];
  if (customerLines.length > 0) {
    context.push(`Kunder och deras pågående projekt:\n${customerLines.join('\n')}`);
  }
  if (activities.length > 0) {
    context.push(`Aktiviteter: ${activities.map((a) => withId(a.Title, a.Id)).join(', ')}`);
  }
  return context;
};

const toId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return parseInt(value, 10);
  return null;
};

// Pulls the trailing ```json block (the structured time report proposal) out of Copilot's answer
const extractSuggestions = (text: string): { answer: string; suggestions: CopilotSuggestion[] } => {
  const match = text.match(/```json\s*([\s\S]*?)```/i);
  if (!match) return { answer: text, suggestions: [] };

  let suggestions: CopilotSuggestion[] = [];
  try {
    const parsed = JSON.parse(match[1]);
    if (Array.isArray(parsed)) {
      suggestions = parsed
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
          customerId: toId(item.customerId),
          projectId: toId(item.projectId),
          activityId: toId(item.activityId),
          hours: Number(String(item.hours ?? '').replace(',', '.')) || 0,
          description: typeof item.description === 'string' ? item.description : '',
        }));
    }
  } catch (err) {
    console.warn('[Copilot] Could not parse suggestion JSON:', err);
  }
  return { answer: text.replace(match[0], '').trim(), suggestions };
};

// Copilot sometimes leaks the JSON nulls into the prose ("Projekt: null") - show them as "Okänd"
const showNullAsUnknown = (text: string): string => text.replace(/\bnull\b/gi, 'Okänd');

// When the editable suggestions are shown, Copilot's own table would just duplicate them
const removeFirstTable = (text: string): string => {
  const lines = text.split('\n');
  const start = lines.findIndex((l) => l.trim().startsWith('|'));
  if (start === -1) return text;
  let end = start;
  for (let i = start; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.startsWith('|')) end = i;
    else if (line) break;
  }
  return [...lines.slice(0, start), ...lines.slice(end + 1)].join('\n').trim();
};

// Copilot's markdown contains entity tags (<Event>, <Person>, <File>) and citation markers ([^1^]) - strip them
const cleanCopilotText = (text: string): string =>
  text
    .replace(/<\/?[A-Za-z]+>/g, '')
    .replace(/\[\^\d+\^\]/g, '')
    .trim();

const LINK_PATTERN = /^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/;

const getLinkSource = (url: string): string => {
  if (url.includes('teams.microsoft.com')) return 'Teams';
  if (url.includes('outlook.')) return 'Outlook';
  if (url.includes('sharepoint.com')) return 'SharePoint';
  try {
    return new URL(url).hostname;
  } catch {
    return 'källan';
  }
};

// Renders **bold** and [text](url) links. Numbered citations become short superscript refs,
// person links (office.com search) are shown as plain names
const renderInline = (line: string): React.ReactNode[] =>
  line.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(https?:\/\/[^)\s]+\))/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    const link = part.match(LINK_PATTERN);
    if (!link) return part;

    const [, label, url] = link;
    if (/^\d+$/.test(label)) {
      return (
        <a key={i} className="copilot-ref" href={url} target="_blank" rel="noopener noreferrer" title={`Öppna i ${getLinkSource(url)}`}>
          {label}
        </a>
      );
    }
    if (url.includes('office.com/search')) return label;
    return <a key={i} href={url} target="_blank" rel="noopener noreferrer">{label}</a>;
  });

const isTableLine = (line: string): boolean => line.startsWith('|');
const isTableSeparator = (line: string): boolean => /^\|[\s:|-]+\|$/.test(line);
const splitTableRow = (line: string): string[] => line.replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());

// Minimal markdown: headings, bullet lists, tables, paragraphs and horizontal rules
const renderMarkdown = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let listItems: string[] = [];
  let tableRows: string[][] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      nodes.push(
        <ul key={`ul-${nodes.length}`}>
          {listItems.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
        </ul>
      );
      listItems = [];
    }
  };

  const flushTable = () => {
    if (tableRows.length > 0) {
      const [header, ...body] = tableRows;
      nodes.push(
        <div key={`table-${nodes.length}`} className="copilot-table-wrapper">
          <table className="copilot-table">
            <thead>
              <tr>{header.map((cell, i) => <th key={i}>{renderInline(cell)}</th>)}</tr>
            </thead>
            <tbody>
              {body.map((row, r) => (
                <tr key={r}>{row.map((cell, c) => <td key={c}>{renderInline(cell)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
    }
  };

  const lines = text.split('\n').map((l) => l.trim());
  lines.forEach((line, index) => {
    // Copilot sometimes puts blank lines between table rows - keep the table together
    if (!line && tableRows.length > 0 && isTableLine(lines.slice(index + 1).find((l) => l) ?? '')) {
      return;
    }
    if (isTableLine(line)) {
      flushList();
      if (!isTableSeparator(line)) tableRows.push(splitTableRow(line));
      return;
    }
    flushTable();

    const bullet = line.match(/^[-*•]\s+(.*)$/);
    const heading = line.match(/^#{1,6}\s+(.*)$/);

    if (bullet) {
      listItems.push(bullet[1]);
      return;
    }
    flushList();

    if (!line || line === '---') return;
    if (heading) {
      nodes.push(<h4 key={nodes.length}>{renderInline(heading[1])}</h4>);
    } else {
      nodes.push(<p key={nodes.length}>{renderInline(line)}</p>);
    }
  });
  flushList();
  flushTable();

  return nodes;
};

// Answers are pre-baked: fetched in the background on load and cached per user and day,
// refreshed in the background when older than this
const CACHE_PREFIX = 'copilotDaySummary:v2:';
const REFRESH_AFTER_MS = 60 * 60 * 1000;

interface CachedSummary {
  answer: string;
  createdAt: string;
  suggestions?: CopilotSuggestion[];
  // Indexes of suggestions already added to the time report, so they aren't added twice
  savedRows?: number[];
}

const toLocalDateKey = (date: Date): string => date.toLocaleDateString('sv-SE');

const getCacheKey = (userId: string): string => `${CACHE_PREFIX}${userId}:${toLocalDateKey(new Date())}`;

const readCache = (key: string): CachedSummary | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (key: string, summary: CachedSummary) => {
  try {
    // Drop earlier days so the cache doesn't grow
    const userPrefix = key.slice(0, key.lastIndexOf(':') + 1);
    Object.keys(localStorage)
      .filter((k) => k.startsWith(userPrefix) && k !== key)
      .forEach((k) => localStorage.removeItem(k));
    localStorage.setItem(key, JSON.stringify(summary));
  } catch {
    // Storage unavailable (private mode etc.) - the answer is still shown, just not cached
  }
};

const loadCatalogContext = async (
  getAccessToken: () => Promise<string | null>,
  currentUser: CurrentUser | null
): Promise<TimeCatalog | null> => {
  try {
    const token = await getAccessToken();
    if (!token) return null;
    return await loadVisibleCatalog(token, currentUser);
  } catch (err) {
    console.warn('[Copilot] Could not load customers/projects/activities:', err);
    return null;
  }
};

// Module-level so a running request survives tab switches/remounts and isn't sent twice
let inFlight: { key: string; promise: Promise<CachedSummary> } | null = null;

const fetchSummary = (
  key: string,
  interactive: boolean,
  getAccessToken: () => Promise<string | null>,
  currentUser: CurrentUser | null
): Promise<CachedSummary> => {
  if (inFlight?.key === key) return inFlight.promise;

  // The lists go inline in the message - Copilot doesn't reliably treat additionalContext as part of the question
  const catalogPromise = loadCatalogContext(getAccessToken, currentUser);
  const message = catalogPromise.then((catalog) => {
    const question = `${PROMPT}\n\n${buildDateScope()}\n\n${TABLE_FORMAT}`;
    const context = catalog ? buildCatalogContext(catalog) : [];
    const text = `${question}\n\n${INSTRUCTIONS}${context.length > 0 ? `\n\n${context.join('\n\n')}` : ''}`;
    console.log('[Copilot] Sending message:\n' + text);
    return text;
  });

  const promise = copilotService
    .ask(message, interactive)
    .then(async (text) => {
      const { answer, suggestions: parsedSuggestions } = extractSuggestions(cleanCopilotText(text));
      const suggestions = filterCustomerTimeSuggestions(parsedSuggestions, await catalogPromise);
      const safeAnswer = suggestions.length === 0
        ? 'Jag kunde inte identifiera någon tydlig kundrelaterad tid att föreslå idag.'
        : suggestions.length !== parsedSuggestions.length
          ? ''
          : answer;
      const summary: CachedSummary = { answer: safeAnswer, suggestions, savedRows: [], createdAt: new Date().toISOString() };
      writeCache(key, summary);
      return summary;
    })
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });

  inFlight = { key, promise };
  return promise;
};

const formatTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });

export interface CopilotStatus {
  loading: boolean;
  // Suggestions not yet added to the time report
  pendingCount: number;
  pendingHours: number;
  createdAt: string | null;
}

interface CopilotDaySummaryProps {
  currentUser: CurrentUser | null;
  onEntriesAdded?: () => void;
  onStatusChange?: (status: CopilotStatus) => void;
  onClose?: () => void;
}

const CopilotDaySummary: React.FC<CopilotDaySummaryProps> = ({ currentUser, onEntriesAdded, onStatusChange, onClose }) => {
  const { getAccessToken, user } = useAuth();
  const cacheKey = user ? getCacheKey(user.homeAccountId) : null;

  const [summary, setSummary] = useState<CachedSummary | null>(() => (cacheKey ? readCache(cacheKey) : null));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsConsent, setNeedsConsent] = useState(false);

  const runFetch = useCallback(
    async (interactive: boolean) => {
      if (!cacheKey) return;
      setLoading(true);
      setError(null);
      setNeedsConsent(false);
      try {
        setSummary(await fetchSummary(cacheKey, interactive, getAccessToken, currentUser));
      } catch (err: any) {
        if (err instanceof CopilotInteractionRequiredError) {
          // Background run can't show a consent popup (browsers block popups without a click)
          console.log('[Copilot] Consent required, waiting for the user to click');
          setNeedsConsent(true);
        } else {
          console.error('[Copilot] Request failed:', err);
          setError(err?.message || 'Kunde inte hämta svar från Copilot');
        }
      } finally {
        setLoading(false);
      }
    },
    [cacheKey, getAccessToken, currentUser]
  );

  // Pre-bake: show today's cached answer instantly, and fetch in the background if there is none yet
  // (or it's from before suggestions existed) or if it's stale. Once something has been added to the
  // time report, a stale answer is kept instead of replaced.
  useEffect(() => {
    if (!cacheKey) return;
    const cached = readCache(cacheKey);
    setSummary(cached);
    const isMissing = !cached || !cached.suggestions;
    const hasSavedRows = (cached?.savedRows?.length ?? 0) > 0;
    const isStale = !!cached && Date.now() - new Date(cached.createdAt).getTime() > REFRESH_AFTER_MS;
    if (isMissing || (isStale && !hasSavedRows)) {
      runFetch(false);
    }
    // getAccessToken changes identity on every render of AuthProvider - only re-run per user/day
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const handleRowsSaved = useCallback(
    (rowIndexes: number[]) => {
      setSummary((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, savedRows: [...(prev.savedRows ?? []), ...rowIndexes] };
        if (cacheKey) writeCache(cacheKey, updated);
        return updated;
      });
    },
    [cacheKey]
  );

  const answer = summary?.answer ?? null;
  const suggestions = summary?.suggestions ?? [];
  const hasSuggestions = suggestions.length > 0;

  // Lets the floating Clippy and the calendar show how many suggestions are waiting
  useEffect(() => {
    const saved = summary?.savedRows ?? [];
    const pending = (summary?.suggestions ?? []).filter((_, i) => !saved.includes(i));
    onStatusChange?.({
      loading,
      pendingCount: pending.length,
      pendingHours: Math.round(pending.reduce((sum, s) => sum + s.hours, 0) * 100) / 100,
      createdAt: summary?.createdAt ?? null,
    });
  }, [loading, summary, onStatusChange]);

  return (
    <div className="copilot-summary">
      <div className="copilot-summary-header">
        <div className="copilot-summary-heading">
          <ClippyIcon busy={loading} />
          <div>
            <h3 className="copilot-summary-title">
              Min dag enligt Clippy, jag menar Copilot <span className="copilot-preview-badge">Preview</span>
            </h3>
            {summary && (
              <p className="copilot-summary-updated">
                {loading ? 'Uppdaterar…' : `Uppdaterad ${formatTime(summary.createdAt)}`}
              </p>
            )}
          </div>
        </div>
        <div className="copilot-summary-actions">
          <button className="copilot-summary-button" onClick={() => runFetch(true)} disabled={loading}>
            {loading ? 'Frågar Copilot…' : answer ? 'Uppdatera' : 'Fråga Copilot'}
          </button>
          {onClose && (
            <button className="copilot-summary-close" onClick={onClose} aria-label="Stäng">×</button>
          )}
        </div>
      </div>

      {error && <div className="copilot-summary-error">{error}</div>}

      {needsConsent && !loading && (
        <p className="copilot-summary-hint">
          Copilot behöver ditt godkännande första gången – klicka på "Fråga Copilot".
        </p>
      )}

      {loading && !answer && (
        <div className="copilot-summary-loading">
          <div className="copilot-skeleton-line" />
          <div className="copilot-skeleton-line" />
          <div className="copilot-skeleton-line short" />
        </div>
      )}

      {summary && hasSuggestions && (
        <div className={loading ? 'is-refreshing' : undefined}>
          <CopilotTimeSuggestions
            suggestions={suggestions}
            savedRows={summary.savedRows ?? []}
            currentUser={currentUser}
            onRowsSaved={handleRowsSaved}
            onEntriesAdded={onEntriesAdded}
          />
        </div>
      )}

      {answer && hasSuggestions && (
        <div className="copilot-summary-details">
          <h4 className="copilot-summary-details-title">Copilots motivering</h4>
          <div className="copilot-summary-answer">{renderMarkdown(stripIds(showNullAsUnknown(removeFirstTable(answer))))}</div>
        </div>
      )}

      {answer && !hasSuggestions && (
        <div className={`copilot-summary-answer${loading ? ' is-refreshing' : ''}`}>
          {renderMarkdown(stripIds(answer))}
        </div>
      )}
    </div>
  );
};

export default CopilotDaySummary;
